import { useState } from 'react';
import { PRIORITIES, type Priority, plural } from '@quadro/domain';
import { confirmAction } from '../../components/Confirm';
import { hud } from '../../components/Hud';
import { Icon } from '../../components/Icon';
import { Options } from '../../components/Options';
import { Sheet, SheetFooter } from '../../components/Sheet';
import * as api from '../../data/api';
import { useAction, useTasks, useTemplates } from '../../data/queries';
import { closeSheet, openSheet } from '../../state/sheets';
import { useWorkspace } from '../../state/workspace';
import { LinksField } from '../board/LinksField';

type Draft = Omit<api.TaskTemplate, 'id'>;
const KEY = [['templates']] as const;
const blank: Draft = { name: '', title: '', description: '', projectId: null, priority: 0, themeIds: [], categoryId: null, subcategoryId: null, entityIds: [], links: [], subtasks: [], dueInDays: null };

/** Modelos de tarefa: lista (usar, editar, excluir) e editor. */
export function TemplatesSheet({ edit, fromTask }: { edit?: string | null; fromTask?: string }) {
  const ws = useWorkspace();
  const templates = useTemplates();
  const tasks = useTasks().data ?? [];
  const [editing, setEditing] = useState<{ id: string | null; draft: Draft } | null>(() => {
    if (fromTask) {
      const t = tasks.find(x => x.id === fromTask);
      if (t) {
        const base = t.due ? Date.parse(`${t.createdAt.slice(0, 10)}T12:00:00Z`) : 0;
        const offset = (d: string | null) => (d && base ? Math.max(0, Math.round((Date.parse(`${d}T12:00:00Z`) - base) / 864e5)) : null);
        return { id: null, draft: {
          ...blank, name: t.title.slice(0, 80), title: t.title, description: t.description, projectId: t.projectId, priority: t.priority,
          themeIds: t.themeIds, categoryId: t.classification?.categoryId ?? null, subcategoryId: t.classification?.subcategoryId ?? null,
          entityIds: t.entityIds, links: t.links, dueInDays: offset(t.due), subtasks: t.subtasks.map(s => ({ text: s.text, offsetDays: offset(s.due) })),
        } };
      }
    }
    if (edit === null) return { id: null, draft: blank };
    return null;
  });
  const [loadedEdit, setLoadedEdit] = useState(false);
  if (edit && !loadedEdit && templates.data) {
    const t = templates.data.find(x => x.id === edit);
    setLoadedEdit(true);
    if (t) { const { id, ...draft } = t; setEditing({ id, draft }); }
  }
  const use = async (t: api.TaskTemplate) => {
    try {
      const { id } = await api.applyTemplate(t.id);
      hud(`Tarefa criada do modelo “${t.name}”`);
      openSheet({ kind: 'task', id });
    } catch (e) { hud((e as Error).message); }
  };
  const remove = useAction(api.deleteTemplate, KEY);

  if (editing) return <Editor initial={editing.draft} id={editing.id} onDone={() => setEditing(null)} />;

  const list = templates.data ?? [];
  return (
    <Sheet onClose={closeSheet} label="Modelos de tarefa">
      <div className="sh-body tp">
        <h2>Modelos de tarefa</h2>
        <p className="muted">Checklists prontos: usar um modelo cria a tarefa com subtarefas, links e prazos contados a partir do dia. Mudar o modelo não altera as tarefas já criadas.</p>
        {!list.length && <p className="muted">{templates.isPending ? 'Carregando…' : 'Nenhum modelo ainda. Crie um aqui ou, numa tarefa pronta, use “Salvar como modelo”.'}</p>}
        <ul className="tp-list">
          {list.map(t => (
            <li key={t.id}>
              <div className="tp-main">
                <strong>{t.name}</strong>
                <span className="muted">{[t.title !== t.name && t.title, t.subtasks.length && plural(t.subtasks.length, 'subtarefa', 'subtarefas'), t.links.length && plural(t.links.length, 'link', 'links'), t.dueInDays != null && `prazo em ${plural(t.dueInDays, 'dia', 'dias')}`].filter(Boolean).join(' · ')}</span>
              </div>
              {ws.canWrite && <>
                <button className="btn" onClick={() => { const { id, ...draft } = t; setEditing({ id, draft }); }}>Editar</button>
                <button className="x-btn" aria-label={`Excluir modelo ${t.name}`} onClick={() => confirmAction({ title: 'Excluir este modelo?', text: `“${t.name}” deixa de aparecer. As tarefas já criadas com ele continuam.`, action: 'Excluir', onConfirm: () => { void remove(t.id); hud('Modelo excluído'); } })}><Icon name="trash" /></button>
                <button className="btn primary" onClick={() => use(t)}>Usar</button>
              </>}
            </li>
          ))}
        </ul>
      </div>
      <SheetFooter>
        <span className="grow" />
        <button className="btn" onClick={closeSheet}>Fechar</button>
        {ws.canWrite && <button className="btn primary" onClick={() => setEditing({ id: null, draft: blank })}>Novo modelo</button>}
      </SheetFooter>
    </Sheet>
  );
}

function Editor({ initial, id, onDone }: { initial: Draft; id: string | null; onDone: () => void }) {
  const ws = useWorkspace();
  const [d, setD] = useState<Draft>(initial);
  const [newSub, setNewSub] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const patch = (p: Partial<Draft>) => setD(x => ({ ...x, ...p }));
  const patchSub = (i: number, p: Partial<api.TemplateSubtask>) => patch({ subtasks: d.subtasks.map((s, j) => (j === i ? { ...s, ...p } : s)) });
  const category = ws.settings.categories.find(c => c.id === d.categoryId);
  const days = (v: string) => (v === '' ? null : Math.max(0, Math.min(365, Math.trunc(Number(v)))));

  async function save() {
    setBusy(true); setError(null);
    try {
      await api.saveTemplate(id, { ...d, name: d.name.trim(), title: d.title.trim() || d.name.trim(), subtasks: d.subtasks.filter(s => s.text.trim()) });
      hud(id ? 'Modelo salvo' : 'Modelo criado');
      onDone();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  }

  return (
    <Sheet wide onClose={closeSheet} label={id ? 'Editar modelo' : 'Novo modelo'}>
      <div className="sh-body tp">
        <h2>{id ? 'Editar modelo' : 'Novo modelo'}</h2>
        <div className="form two">
          <label htmlFor="tpName">Modelo</label>
          <input id="tpName" className="full" autoFocus maxLength={80} placeholder="Ex.: Checklist de fechamento" value={d.name} onChange={e => patch({ name: e.target.value })} />
          <label htmlFor="tpTitle">Título</label>
          <input id="tpTitle" className="full" maxLength={160} placeholder="Título da tarefa criada (padrão: o nome do modelo)" value={d.title} onChange={e => patch({ title: e.target.value })} />
          <label htmlFor="tpProj">Projeto</label>
          <select id="tpProj" value={d.projectId ?? ''} onChange={e => patch({ projectId: e.target.value || null })}><Options items={[['', 'Sem projeto'], ...ws.settings.projects.map(p => [p.id, p.name] as [string, string])]} /></select>
          <label htmlFor="tpPrio">Prioridade</label>
          <select id="tpPrio" value={d.priority} onChange={e => patch({ priority: Number(e.target.value) as Priority })}><Options items={PRIORITIES.map((p, i) => [i, p])} /></select>
          <label htmlFor="tpDue">Prazo</label>
          <div className="pair"><input id="tpDue" type="number" min={0} max={365} style={{ width: 80 }} placeholder="—" value={d.dueInDays ?? ''} onChange={e => patch({ dueInDays: days(e.target.value) })} /><span className="muted">dias depois de criada</span></div>
          {ws.settings.categories.length > 0 ? <>
            <label htmlFor="tpCat">Categoria</label>
            <select id="tpCat" value={d.categoryId ?? ''} onChange={e => patch({ categoryId: e.target.value || null, subcategoryId: null })}><Options items={[['', 'Não classificada'], ...ws.settings.categories.map(c => [c.id, c.name] as [string, string])]} /></select>
            {category && <><label htmlFor="tpSub">Subcategoria</label>
              <select id="tpSub" value={d.subcategoryId ?? ''} onChange={e => patch({ subcategoryId: e.target.value || null })}><Options items={[['', 'Nenhuma'], ...category.subcategories.map(s => [s.id, s.name] as [string, string])]} /></select></>}
          </> : <><span /><span /></>}
          {ws.settings.themes.length > 0 && <><label>Temas</label>
            <div className="full tema-picker">{ws.settings.themes.map(th => (
              <button key={th.id} type="button" className="tchip pick" style={{ '--tc': th.color } as React.CSSProperties} aria-pressed={d.themeIds.includes(th.id)}
                onClick={() => patch({ themeIds: d.themeIds.includes(th.id) ? d.themeIds.filter(x => x !== th.id) : [...d.themeIds, th.id] })}>{th.name}</button>
            ))}</div></>}
          <label>Links</label>
          <div className="full"><LinksField value={d.links} onChange={links => patch({ links })} editable /></div>
          <label htmlFor="tpDesc">Descrição</label>
          <textarea id="tpDesc" rows={3} className="full" value={d.description} onChange={e => patch({ description: e.target.value })} />
        </div>
        <h3>Subtarefas</h3>
        {d.subtasks.map((s, i) => (
          <div className="st tp-st" key={i}>
            <span className="tp-n">{i + 1}.</span>
            <input className="st-t" value={s.text} aria-label="Subtarefa do modelo" maxLength={300} onChange={e => patchSub(i, { text: e.target.value })} />
            <span className="muted">em</span>
            <input type="number" min={0} max={365} className="tp-days" aria-label="Prazo da subtarefa em dias" placeholder="—" value={s.offsetDays ?? ''} onChange={e => patchSub(i, { offsetDays: days(e.target.value) })} />
            <span className="muted">dias</span>
            <button className="x-btn" aria-label="Remover subtarefa" onClick={() => patch({ subtasks: d.subtasks.filter((_, j) => j !== i) })}><Icon name="x" /></button>
          </div>
        ))}
        <div className="st-add"><Icon name="plus" />
          <input placeholder="Nova subtarefa e Enter" aria-label="Nova subtarefa do modelo" maxLength={300} value={newSub} onChange={e => setNewSub(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && newSub.trim()) { e.preventDefault(); patch({ subtasks: [...d.subtasks, { text: newSub.trim(), offsetDays: null }] }); setNewSub(''); } }} />
        </div>
        {error && <p className="auth-error" role="alert">{error}</p>}
      </div>
      <SheetFooter>
        <button className="btn" onClick={onDone}>Voltar</button>
        <span className="grow" />
        <button className="btn" onClick={closeSheet}>Cancelar</button>
        <button className="btn primary" disabled={busy || !d.name.trim()} onClick={save}>{busy ? 'Salvando…' : 'Salvar modelo'}</button>
      </SheetFooter>
    </Sheet>
  );
}
